/* ============================================================
   Победители — зал славы: подиум (золото приподнято),
   лучший бомбардир, статы турнира, чемпионы всех турниров.
   ============================================================ */
import { useFedStore, useL } from '../store';
import { useFedContent } from '../content';
import { WN_STATS, flag } from '../data';
import { Ic } from '../icons';
import { Rv } from '../ui';

const WN_STYLE = {
  gold: {
    pad: '38px 24px 30px',
    bg: 'linear-gradient(170deg,rgba(230,183,60,.16),rgba(23,21,16,.6) 32%,#131316 72%)',
    border: '1.5px solid rgba(230,183,60,.6)',
    shadow: '0 34px 70px -28px rgba(0,0,0,.9),0 0 44px -12px rgba(230,183,60,.4)',
    lift: 'translateY(-18px)',
    medal: 50, labColor: '#E6B73C', flagW: 84, teamSize: 34, delay: 0,
  },
  silver: {
    pad: '28px 20px 24px',
    bg: 'linear-gradient(165deg,#1A1A20,#121216)',
    border: '1px solid rgba(255,255,255,.1)',
    shadow: 'var(--fed-shadow)',
    lift: 'none',
    medal: 40, labColor: '#B9C2CC', flagW: 62, teamSize: 25, delay: 0.12,
  },
  bronze: {
    pad: '28px 20px 24px',
    bg: 'linear-gradient(165deg,#1A1A20,#121216)',
    border: '1px solid rgba(255,255,255,.1)',
    shadow: 'var(--fed-shadow)',
    lift: 'none',
    medal: 40, labColor: '#D89B62', flagW: 62, teamSize: 25, delay: 0.18,
  },
};

export default function FedWinners() {
  const { lang } = useFedStore();
  const L = useL();
  const winners = useFedContent((s) => s.winners);
  const tournaments = useFedContent((s) => s.tournaments);

  const champions = tournaments.filter((t) => t.status === 'done' && t.champ);

  // Раскладку подиума задаёт медаль, а не порядок в админке: чемпион всегда
  // в центре (десктоп, широкая средняя колонка) и наверху (мобайл, order:-1 у
  // 2-го элемента). Поэтому сортируем в порядок [серебро, золото, бронза]
  // независимо от того, как записи переставлены в списке.
  const PODIUM_ORDER = { silver: 0, gold: 1, bronze: 2 };
  const podium = [...winners].sort((a, b) => (PODIUM_ORDER[a.cls] ?? 9) - (PODIUM_ORDER[b.cls] ?? 9));

  return (
    <div className="fed-pagewrap max-w-[1200px] mx-auto" style={{ padding: '52px 24px 72px' }}>
      {/* заголовок */}
      <Rv className="text-center" style={{ marginBottom: 40 }}>
        <div className="font-mono uppercase text-gold" style={{ fontSize: 10.5, letterSpacing: '.2em' }}>{L.wn_hall}</div>
        <h1
          style={{
            fontSize: 'clamp(38px,8vw,90px)', marginTop: 14, overflowWrap: 'anywhere',
            background: 'linear-gradient(120deg,#fff 20%,var(--fed-gold-bright) 60%,var(--fed-gold-deep))',
            WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}
        >
          {L.wn_h1}
        </h1>
        <p className="text-fg-soft" style={{ fontSize: 15, marginTop: 12 }}>{L.wn_sub}</p>
      </Rv>

      {/* подиум */}
      <div className="fed-wn-podium">
        {podium.map((w) => {
          const st = WN_STYLE[w.cls] || WN_STYLE.bronze;
          return (
            <Rv
              key={w.id || w.place}
              delay={st.delay}
              className="relative flex flex-col items-center gap-2.5 text-center overflow-hidden rounded-[20px]"
              style={{ padding: st.pad, background: st.bg, border: st.border, boxShadow: st.shadow, transform: st.lift }}
            >
              <Ic name="medal" size={st.medal} color={st.labColor} />
              <span className="font-mono font-bold" style={{ fontSize: 10, letterSpacing: '.28em', color: st.labColor }}>{w['lab_' + lang]}</span>
              {w.cc ? <img src={flag(w.cc)} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} className="rounded-md" style={{ width: st.flagW, boxShadow: '0 8px 22px -8px rgba(0,0,0,.8),0 0 0 1px rgba(255,255,255,.12)' }} /> : null}
              <span className="font-display font-bold text-white" style={{ fontSize: st.teamSize }}>{w.team}</span>
              <span className="font-semibold text-fg-soft" style={{ fontSize: 14 }}>{w.player}</span>
              <span className="font-mono uppercase text-fg-muted" style={{ fontSize: 9.5, letterSpacing: '.1em' }}>{w['note_' + lang]}</span>
              <span className="absolute font-display font-bold" style={{ top: 12, right: 16, fontSize: 44, color: st.labColor, opacity: 0.18 }}>{w.place}</span>
            </Rv>
          );
        })}
      </div>

      {/* бомбардир */}
      <Rv
        className="flex items-center justify-center gap-2.5 bg-surface rounded-full mx-auto"
        style={{ margin: '34px auto 0', maxWidth: 520, border: '1px solid rgba(230,183,60,.4)', padding: '13px 26px', boxShadow: '0 0 26px -12px rgba(230,183,60,.4)' }}
      >
        <Ic name="ball" size={18} color="var(--fed-gold)" />
        <span className="text-fg-soft text-center" style={{ fontSize: 14.5 }}>
          {L.wn_scorer} — <b className="text-white">Влад А. (Босния)</b> · 42 {L.wn_goals_word}
        </span>
      </Rv>

      {/* статы */}
      <Rv className="grid gap-3.5 mx-auto" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', maxWidth: 760, margin: '26px auto 0' }}>
        {WN_STATS.map((s) => (
          <div key={s[0]} className="text-center bg-surface border border-line rounded-2xl" style={{ padding: '18px 10px' }}>
            <div className="font-display font-bold text-gold" style={{ fontSize: 32 }}>{s[0]}</div>
            <div className="font-mono uppercase text-fg-muted" style={{ fontSize: 9.5, letterSpacing: '.16em', marginTop: 5 }}>{lang === 'ru' ? s[1] : s[2]}</div>
          </div>
        ))}
      </Rv>

      {/* чемпионы турниров */}
      <div style={{ marginTop: 52 }}>
        <Rv as="h2" className="text-center" style={{ fontSize: 'clamp(24px,3.4vw,34px)', marginBottom: 24 }}>
          {L.wn_h1} · {L.brandTop}
        </Rv>
        <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))' }}>
          {champions.map((c) => (
            <Rv key={c.id} className="flex items-center gap-4 rounded-2xl border border-line" style={{ padding: 20, background: 'linear-gradient(160deg,var(--fed-surface-2),var(--fed-surface))' }}>
              <Ic name="trophy" size={30} color="var(--fed-gold)" />
              <div className="min-w-0 flex-1">
                <div className="font-mono uppercase text-gold" style={{ fontSize: 10, letterSpacing: '.14em' }}>{c.disc} · {c.season}</div>
                <div className="font-display font-bold text-fg" style={{ fontSize: 19, marginTop: 5 }}>{c.title}</div>
                <div className="flex items-center gap-2" style={{ marginTop: 7 }}>
                  {c.champCc ? <img src={flag(c.champCc)} alt="" className="rounded-[3px]" style={{ width: 22 }} /> : null}
                  <span className="font-semibold text-fg-soft" style={{ fontSize: 14 }}>{c.champ}</span>
                  <span className="text-fg-muted" style={{ fontSize: 12.5 }}>· {c.champPlayer}</span>
                </div>
              </div>
            </Rv>
          ))}
        </div>
      </div>
    </div>
  );
}
